import * as XLSX from "xlsx";
import axios from "axios";

const REQUIRED_COLUMNS = ["name", "phoneNumber", "categorySlug", "experience"];

const TEMPLATE_ROWS = [
  {
    name: "Sara Ahmed",
    name_amh: "ሳራ አህመድ",
    phoneNumber: "0911123456",
    alternativePhone: "0922113344",
    gender: "Female",
    dob: "1995-04-12",
    experience: 3,
    fees: 500,
    languages: "Amharic, English",
    subcity: "Bole",
    addressLine1: "Atlas",
    addressCity: "Addis Ababa",
    nationality: "Ethiopian",
    education: "Diploma",
    categorySlug: "on_demand_nanny",
    addressCity_amh: "አዲስ አበባ",
    addressLine1_amh: "አትላስ",
    education_amh: "ዲፕሎማ",
    languages_amh: "አማርኛ, እንግሊዝኛ",
    nationality_amh: "ኢትዮጵያዊ",
  },
  {
    name: "Mekdes Tesfaye",
    name_amh: "መቅደስ ተስፋዬ",
    phoneNumber: "0933123456",
    alternativePhone: "",
    gender: "Female",
    dob: "1990-08-20",
    experience: 5,
    fees: 6000,
    languages: "Amharic",
    subcity: "Yeka",
    addressLine1: "Megenagna",
    addressCity: "Addis Ababa",
    nationality: "Ethiopian",
    education: "Certificate",
    categorySlug: "full_time_nanny",
    addressCity_amh: "አዲስ አበባ",
    addressLine1_amh: "መገናኛ",
    education_amh: "ሰርተፊኬት",
    languages_amh: "አማርኛ",
    nationality_amh: "ኢትዮጵያዊ",
  },
  {
    name: "Aster Tolosa",
    name_amh: "አስቴር ቶሎሳ",
    phoneNumber: "0944123456",
    alternativePhone: "",
    gender: "Female",
    dob: "1998-05-15",
    experience: 2,
    fees: 6000,
    languages: "Amharic, Afaan Oromoo",
    subcity: "Kolfe Keranio",
    addressLine1: "Ayat",
    addressCity: "Addis Ababa",
    nationality: "Ethiopian",
    education: "Diploma",
    categorySlug: "after_school_care",
    addressCity_amh: "አዲስ አበባ",
    addressLine1_amh: "አያት",
    education_amh: "ዲፕሎማ",
    languages_amh: "አማርኛ, ኦሮምኛ",
    nationality_amh: "ኢትዮጵያዊ",
  },
];

const normalizeHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const getValue = (row, aliases) => {
  const normalized = Object.entries(row).reduce((acc, [key, value]) => {
    acc[normalizeHeader(key)] = value;
    return acc;
  }, {});

  for (const alias of aliases) {
    const value = normalized[normalizeHeader(alias)];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

const normalizePhone = (value) => {
  const digits = String(value || "").replace(/[^\d]/g, "");
  if (digits.length === 9) return `0${digits}`;
  if (digits.length === 12 && digits.startsWith("251"))
    return `0${digits.slice(3)}`;
  return digits;
};

export const generateTemplate = () => TEMPLATE_ROWS;

export const downloadTemplate = () => {
  const worksheet = XLSX.utils.json_to_sheet(generateTemplate());
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Caregivers");

  worksheet["!cols"] = Object.keys(generateTemplate()[0]).map(() => ({
    wch: 22,
  }));

  XLSX.writeFile(workbook, "caregivers_import_template.xlsx");
};

export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        resolve(validateAndTransformData(jsonData));
      } catch (err) {
        reject(new Error("Failed to parse import file"));
      }
    };

    reader.onerror = () => reject(new Error("File reading error"));
    reader.readAsArrayBuffer(file);
  });
};

const validateAndTransformData = (excelData) => {
  const validatedData = [];
  const errors = [];
  const seenPhones = new Set();

  excelData.forEach((row, index) => {
    const rowNumber = index + 2;
    const rowErrors = [];
    const isEmptyRow = Object.values(row).every(
      (value) => value === "" || value === null || value === undefined,
    );
    if (isEmptyRow) return;

    const name = String(getValue(row, ["name", "full name"]) || "").trim();
    const phoneNumber = normalizePhone(
      getValue(row, ["phoneNumber", "phone number", "phone"]),
    );
    const categorySlug = String(
      getValue(row, [
        "categorySlug",
        "category slug",
        "category",
        "service category",
        "service type",
      ]) || "",
    ).trim();
    const experience = getValue(row, ["experience", "experience years"]);
    const fees = getValue(row, ["fees", "fee", "salary"]);

    if (!name) rowErrors.push(`Row ${rowNumber}: name is required`);
    if (!phoneNumber)
      rowErrors.push(`Row ${rowNumber}: phoneNumber is required`);
    if (phoneNumber && !/^\d{10}$/.test(phoneNumber)) {
      rowErrors.push(`Row ${rowNumber}: phoneNumber must be 10 digits`);
    }
    if (phoneNumber && seenPhones.has(phoneNumber)) {
      rowErrors.push(`Row ${rowNumber}: duplicate phoneNumber in file`);
    }
    if (!categorySlug) {
      rowErrors.push(`Row ${rowNumber}: categorySlug is required`);
    }

    const expNum = Number(experience);
    if (
      experience === "" ||
      Number.isNaN(expNum) ||
      expNum < 0 ||
      expNum > 60
    ) {
      rowErrors.push(`Row ${rowNumber}: experience must be between 0 and 60`);
    }

    if (fees !== "") {
      const feesNum = Number(fees);
      if (Number.isNaN(feesNum) || feesNum < 0) {
        rowErrors.push(`Row ${rowNumber}: fees must be a positive number`);
      }
    }

    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
      return;
    }

    seenPhones.add(phoneNumber);
    validatedData.push({
      ...row,
      name,
      phoneNumber,
      categorySlug,
      experience: expNum,
    });
  });

  return { validatedData, errors, requiredColumns: REQUIRED_COLUMNS };
};

export const importCaregivers = async (
  file,
  backendUrl,
  aToken,
  onProgress,
) => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    onProgress?.(20);
    const { data } = await axios.post(
      `${backendUrl}/api/admin/caregivers/import`,
      formData,
      {
        headers: { aToken },
      },
    );

    onProgress?.(100);
    const results = data.results || {};
    return {
      success: results.uploaded ?? results.success ?? 0,
      uploaded: results.uploaded ?? results.success ?? 0,
      skipped: results.skipped ?? 0,
      failed: results.failed ?? 0,
      errors: results.errors || [],
      totalRows: results.totalRows ?? 0,
    };
  } catch (err) {
    onProgress?.(100);
    const apiErrors = err.response?.data?.errors || [];
    return {
      success: 0,
      uploaded: 0,
      skipped: 0,
      failed: 1,
      errors: apiErrors.length
        ? apiErrors
        : [{ error: err.response?.data?.message || err.message }],
      totalRows: 0,
    };
  }
};
